import { useCallback } from 'react';
import { create } from 'zustand';
import WidgetRenderer, { WIDGET_ANSWER_TYPES, type WidgetStateSnapshot } from '../../lab/WidgetRenderer';
import type { ActivityStep, WidgetRefConfig } from '../classroomApi';
import { widgetRefsForStep } from '../widgetForStep';

interface StepPromptProps {
  step: ActivityStep;
  widgetRefs: WidgetRefConfig[];
}

// WIP-016 (design §8.3) — a tiny, ephemeral bridge between this file (the one
// place a participating widget's `onStateChange` fires, since this is where
// `widget_refs` are rendered) and `ResponseControl`'s widget-submit control
// (build order §2.3), which needs the latest snapshot to build the payload.
// The two are sibling components under `LiveStep` in `index.tsx` — frozen by
// WIP-018 for this set (build order §5) — so neither prop-drilling through it
// nor a new `store.ts` selector (also frozen) is available; this store is the
// smallest thing that closes the gap. Keyed by step: `useWidgetSnapshot` only
// ever returns a snapshot that belongs to the step asking for it, so moving
// to a different live step can never let a stale snapshot be submitted as
// today's answer.
interface WidgetSnapshotState {
  stepKey: string | null;
  snapshot: WidgetStateSnapshot | null;
  set: (stepKey: string, snapshot: WidgetStateSnapshot) => void;
}

const useWidgetSnapshotStore = create<WidgetSnapshotState>((set) => ({
  stepKey: null,
  snapshot: null,
  set: (stepKey, snapshot) => set({ stepKey, snapshot }),
}));

/** Read by `ResponseControl`'s widget-submit control. Returns `null` unless
 *  the latest captured snapshot belongs to `stepKey`. */
export function useWidgetSnapshot(stepKey: string): WidgetStateSnapshot | null {
  return useWidgetSnapshotStore((s) => (s.stepKey === stepKey ? s.snapshot : null));
}

// The live step's prompt text plus any widget_refs the activity carries,
// rendered through the existing WidgetRenderer (BP 09 catalog) — this feature
// never renders a widget of its own, only choreographs the real ones.
//
// WIP-016: when the step's answer IS a widget (`step.answer === 'widget'`)
// and the activity's first widget_ref is one of the widgets that implements
// the state-capture contract (`WIDGET_ANSWER_TYPES` — verified against the
// real catalogue by WidgetRenderer's own test), that widget's `onStateChange`
// is wired to the bridge above. A widget outside that set, or a step that
// doesn't opt in, renders exactly as before: a plain, uninstrumented preview.
export default function StepPrompt({ step, widgetRefs }: StepPromptProps) {
  // A step's OWN widget wins over the activity's. The card sort has sixteen
  // prompts and had one widget between them, so card 16 showed card 1's model
  // — see ../widgetForStep.ts for the whole story, including what this does
  // NOT fix (the card content itself does not exist).
  const refs = widgetRefsForStep(step, widgetRefs);
  const primaryRef = refs[0];
  const isWidgetAnswer = step.answer === 'widget' && !!primaryRef && WIDGET_ANSWER_TYPES.has(primaryRef.type);

  const handleStateChange = useCallback(
    (snapshot: WidgetStateSnapshot) => useWidgetSnapshotStore.getState().set(step.key, snapshot),
    [step.key],
  );

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-ws-ink">{step.prompt}</p>
      {refs.length > 0 && (
        <div className="space-y-3">
          {refs.map((w, i) => (
            // Keyed by what actually identifies this widget, never by array
            // index. With key={i}, switching activities left index 0 as index
            // 0, so React reused the mounted component instead of replacing
            // it — a card-sort step kept showing the warm-up poll's particle
            // model, still carrying the previous widget's internal state.
            // step.key + type + config changes whenever the widget genuinely
            // differs, which forces a real remount; when all three match, the
            // widget IS the same one and reuse is correct.
            <div
              key={`${step.key}:${w.type}:${JSON.stringify(w.config ?? {})}:${i}`}
              className="overflow-hidden rounded-lg border border-ws-border"
            >
              <WidgetRenderer
                widget={{ type: w.type, config: w.config }}
                onStateChange={isWidgetAnswer && i === 0 ? handleStateChange : undefined}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
